import { PublicClientApplication } from "@azure/msal-browser";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import * as microsoftTeams from "@microsoft/teams-js";
import React, { useEffect, useState } from "react";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { RootLayout } from "../../../components-v2/common/root-layout/RootLayout";
import Flight360 from "../../../components-v2/flights/Flight360";
import { MultiAirport } from "../../../components-v2/multi-airport-list/MultiAirport";
import NotificationHistory from "../../../components-v2/notifications/history-page/NotificationHistory";
import { Overview } from "../../../components-v2/overview/Overview";
import SingleFlightOverview from "../../../components-v2/single-flight-dashboard/SingleFlightOverview";
import LayoutStateProvider from "../../../context/LayoutStateProvider";
import { NotificationWebSocketProvider } from "../../../context/NotificationWebSocketContext";
import PageEnum from "../../../enum/PageEnum";
import { useApiCall } from "../../../hooks/useApiCall";
import { airportCodeActions } from "../../../store/airport/AirportCodeSlice";
import { getAirportCode } from "../../../store/airport/AirportSelector";
import { useAppDispatch, useAppSelector } from "../../../store/StoreHooks";
import { setAirportConfiguration } from "../../../utils/common/AirportConfiguration";
import {
  getAirportCodeHelper,
  getLayoutClass,
  isInTeams,
  isValidTenant,
} from "../../../utils/common/CommonHelperFunctions";
import {
  AIRPORT_CODE_PARAMETER,
  allowedPathsByAirport,
} from "../../../utils/common/Constants";
import { AdminContainer } from "../../admin/AdminContainer";
import { AppSettings } from "../../app-settings/AppSettings";
import { Baggage } from "../../baggage/Baggage";
import { CrisisDashboard } from "../../events/crisis-dashboard/CrisisDashboard";
import { EventTasks } from "../../events/event-container/event-item/event-tasks/EventTasks";
import { EventContainer } from "../../events/event-container/EventContainer";
import { PAX } from "../../pax/PAX";
import { ResourceAllocation } from "../../resources-allocation/ResourceAllocation";
import { Weather } from "../../weather/Weather";
import { AccessDenied } from "../access-denied/AccessDenied";
import { LoadingScreen } from "../loading-screen/LoadingScreen";
import { NavBar } from "../nav-bar/NavBar";
import { SignIn } from "../sign-in/SignIn";
import AppSideBar from "./AppSideBar";

export const APP_SIDEBAR_HIDDEN_PATHS = [`/${PageEnum.PageUrl.MultiAirports}`];
const TEAMS_WEB_MESSAGE_ORIGINS = [""];

export const PageLayout: React.FunctionComponent = () => {
  const isAuthenticated = useIsAuthenticated(); // Used only for Web App, not for Teams.
  const [userTenantId, setUserTenantId] = useState<string | undefined>("");
  const [airportConfigState, setAirportConfigState] = useState<any[]>([]);

  const [userEmailId, setUserEmailId] = useState<string | undefined>("");
  const allowedTenants = process.env.REACT_APP_ALLOWED_TENANTIDS
    ? process.env.REACT_APP_ALLOWED_TENANTIDS.toString().split(";")
    : [];

  const { accounts } = useMsal();

  const location = useLocation() ?? "";
  const hideAppSidebar = APP_SIDEBAR_HIDDEN_PATHS.some(
    (path) =>
      location.pathname === path || location.pathname.startsWith(`${path}/`)
  );

  const navigate = useNavigate();
  const isInTeamsContext = isInTeams();
  const isAirportConfigLoaded =
    airportConfigState && airportConfigState.length > 0;

  useEffect(() => {
    if (isInTeamsContext) {
      microsoftTeams.initialize(() => {
        microsoftTeams.getContext((context) => {
          setUserTenantId(context.tid);
          setUserEmailId(context.userPrincipalName);
        });
      }, TEAMS_WEB_MESSAGE_ORIGINS);
    } else if (accounts.length > 0) {
      setUserTenantId(accounts[0].tenantId);
      setUserEmailId(accounts[0].username);
    }
  }, [accounts, isInTeamsContext]);

  const isValidTenantId = isValidTenant(userTenantId, allowedTenants);

  const msalInstance = useMsal().instance as PublicClientApplication;
  const dispatch = useAppDispatch();

  const airport = useAppSelector((state) => getAirportCode(state));

  //To be changed later. right now hardcoded as "XYZ"
  const getAirportConfigurationUrl =
    userTenantId && userEmailId
      ? `${process.env.REACT_APP_API_AIRPORT_CONFIGURATION?.replace(
          AIRPORT_CODE_PARAMETER,
          "XYZ"
        )
          .replace("{tenantId}", userTenantId)
          .replace("{emailId}", userEmailId)}`
      : null;

  const { data: airportConfigurationResponse } = useApiCall<any>({
    url: getAirportConfigurationUrl || "",
    dependencies: [getAirportConfigurationUrl],
  });

  useEffect(() => {
    if (airportConfigurationResponse) {
      setAirportConfiguration(airportConfigurationResponse); // for functions
      setAirportConfigState(airportConfigurationResponse); // for components
      getAirportCodeHelper(msalInstance).then((airportCode: string) => {
        dispatch(airportCodeActions.setAirportCode({ airportCode }));
      });
    }
  }, [airportConfigurationResponse]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const airportParam = params.get("airport")?.toUpperCase();

    const normalizePath = (path: string) => {
      const collapsed = path.replace(/\/{2,}/g, "/");
      return collapsed !== "/" ? collapsed.replace(/\/$/, "") : "/";
    };

    const getCurrentAirport = async () => {
      const currentAirport =
        airportParam ||
        sessionStorage.getItem("airportCode") ||
        (await getAirportCodeHelper(msalInstance));

      if (!currentAirport) {
        return;
      }

      const allowedPaths = allowedPathsByAirport[currentAirport] || [];
      const currentPath = normalizePath(location.pathname);

      const isAllowed = allowedPaths.some((path: string | RegExp) => {
        if (typeof path === "string") {
          const fullPath = normalizePath(
            path.startsWith("/") ? path : `/${path}`
          );
          return currentPath === fullPath;
        }
        if (path instanceof RegExp) {
          return path.test(currentPath);
        }
        return false;
      });

      if (!isAllowed) {
        navigate(`/${PageEnum.PageUrl.Home}`, { replace: true });
      } else {
        sessionStorage.setItem("airportCode", currentAirport);
        dispatch(
          airportCodeActions.setAirportCode({ airportCode: currentAirport })
        );
      }
    };

    getCurrentAirport();
  }, [airport, location.pathname, location.search, navigate, dispatch]);

  return (
    <>
      {(!airport || !isAirportConfigLoaded) && (
        <LoadingScreen message="Please wait..." />
      )}
      {!isAuthenticated && !isInTeamsContext && <SignIn />}
      {(isAuthenticated || isInTeamsContext) && isAirportConfigLoaded && (
        <>
          {!isValidTenantId && userTenantId && <AccessDenied />}
          {!isValidTenantId && !userTenantId && (
            <LoadingScreen message="Please wait.." />
          )}

          {isValidTenantId && (
            <LayoutStateProvider>
              <NotificationWebSocketProvider airport={airport}>
                {!isInTeamsContext ? <NavBar /> : <></>}
                <section
                  className={`amd-maincontainer ${getLayoutClass(isInTeamsContext)}`}
                >
                  <main
                    className="amd-main"
                    id="back-to-top-anchor"
                    style={{ scrollMarginTop: "30px" }}
                  >
                    {airport && (
                      <Routes>
                        <Route path="/" element={<RootLayout />}>
                          <Route
                            index
                            element={
                              <Navigate
                                to={`${PageEnum.PageUrl.Home}`}
                                replace
                              />
                            }
                          />
                          <Route
                            path={`${PageEnum.PageUrl.MultiAirports}`}
                            element={
                              <MultiAirport
                                airportConfiguration={airportConfigState}
                              />
                            }
                          />
                          <Route
                            path={`${PageEnum.PageUrl.Home}`}
                            element={<Overview airport={airport} />}
                          />
                          <Route
                            path={`${PageEnum.PageUrl.Flights}`}
                            element={<Flight360 />}
                          />
                          <Route
                            path={`/${PageEnum.PageUrl.Baggage}`}
                            element={<Baggage />}
                          />
                          <Route
                            path={`/${PageEnum.PageUrl.Passenger}`}
                            element={<PAX />}
                          />
                          <Route
                            path={`/${PageEnum.PageUrl.Weather}`}
                            element={<Weather />}
                          />
                          <Route
                            path={`/${PageEnum.PageUrl.EventManagement}`}
                            element={<EventContainer />}
                          />
                          <Route
                            path={`/${PageEnum.PageUrl.Notifications}`}
                            element={<NotificationHistory />}
                          />
                          <Route
                            path={`/${PageEnum.PageUrl.ResourceAllocation}`}
                            element={<ResourceAllocation />}
                          />
                          <Route
                            path={`/${PageEnum.PageUrl.Admin}`}
                            element={<AdminContainer airport={airport} />}
                          />
                          <Route
                            path={`/${PageEnum.PageUrl.ApplicationSettings}`}
                            element={<AppSettings airportCode={airport} />}
                          />
                          <Route path="/event-tasks" element={<EventTasks />} />
                          <Route
                            path="/crisis-dashboard"
                            element={<CrisisDashboard />}
                          />
                          <Route
                            path="/single-flight-view"
                            element={
                              <SingleFlightOverview airportCode={airport} />
                            }
                          />
                        </Route>

                        <Route
                          path="/"
                          element={
                            <Navigate
                              to={`/${PageEnum.PageUrl.Home}`}
                              replace
                            />
                          }
                        />
                        <Route
                          path="*"
                          element={
                            <Navigate
                              to={`/${PageEnum.PageUrl.Home}`}
                              replace
                            />
                          }
                        />
                      </Routes>
                    )}
                  </main>
                  {!hideAppSidebar && <AppSideBar />}
                </section>
              </NotificationWebSocketProvider>
            </LayoutStateProvider>
          )}
        </>
      )}
    </>
  );
};
